import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setAppBadge } from '../utils/appBadge';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds

export function NotificationProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);

  // Mirror the unread count onto the app icon. Driven from state rather than
  // from each call site, so it stays right however the count changed — polled,
  // marked read here, or read somewhere else entirely.
  useEffect(() => { setAppBadge(unreadCount); }, [unreadCount]);
  const [notifications, setNotifications] = useState([]);
  // Unread per family (the aside's pills) and the inbox's full size, from the last list
  // fetch — both ignore whatever filter that fetch used.
  const [unreadByFamily, setUnreadByFamily] = useState({});
  const [allCount, setAllCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef(null);

  // Fetch unread count (lightweight polling target)
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    const result = await notificationService.getUnreadCount();
    if (result.success && result.data) {
      setUnreadCount(result.data.unreadCount);
    }
  }, [isAuthenticated]);

  // Fetch paginated notifications. `filter` is { family, unread } (see
  // notificationService); `keep: false` leaves the shared list alone so the page can
  // hold its own filtered copy without the dropdown's fetch clobbering it.
  const fetchNotifications = useCallback(async (page = 1, pageSize = 20, filter = {}, { keep = true } = {}) => {
    if (!isAuthenticated) return null;
    setIsLoading(true);
    const result = await notificationService.getNotifications(page, pageSize, filter);
    setIsLoading(false);

    if (result.success && result.data) {
      if (keep) {
        if (page === 1) {
          setNotifications(result.data.notifications);
        } else {
          setNotifications(prev => [...prev, ...result.data.notifications]);
        }
      }
      setUnreadCount(result.data.unreadCount);
      if (result.data.unreadByFamily) setUnreadByFamily(result.data.unreadByFamily);
      if (typeof result.data.allCount === 'number') setAllCount(result.data.allCount);
      return result.data;
    }
    return null;
  }, [isAuthenticated]);

  // Mark single notification as read (optimistic). `family` keeps the aside's count
  // honest when the row isn't in the shared list.
  const markAsRead = useCallback(async (id, family = null) => {
    // Optimistic update
    let fam = family;
    setNotifications(prev =>
      prev.map(n => {
        if (n.id !== id) return n;
        fam = fam || n.family || null;
        return { ...n, isRead: true };
      })
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    if (fam) setUnreadByFamily(prev => ({ ...prev, [fam]: Math.max(0, (prev[fam] || 0) - 1) }));

    const result = await notificationService.markAsRead(id);
    if (!result.success) {
      // Revert on failure
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  // Mark all as read (optimistic)
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    setUnreadByFamily(prev => Object.fromEntries(Object.keys(prev).map(k => [k, 0])));

    const result = await notificationService.markAllAsRead();
    if (!result.success) {
      // Revert on failure
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  // Poll for unread count when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchUnreadCount();
      pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    } else {
      setUnreadCount(0);
      setNotifications([]);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isAuthenticated, authLoading, fetchUnreadCount]);

  const value = {
    unreadCount,
    unreadByFamily,
    allCount,
    notifications,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
